// 🎯 Hook personalizado para el detalle de órdenes de ubicación - Custom hook for location order details
import { createHttpClient } from '@shared/adapters/HttpClient';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReceiveApiAdapter } from '../adapters/ReceiveApiAdapter';

export const useReceiveReturned = (returnedId: number) => {
  const { t } = useTranslation('locationordersDetailModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const receiveAdapter = new ReceiveApiAdapter(createHttpClient(apiClientWH));

  // 🎯 Query para obtener datos de la orden de ubicación
  const {
    data: locationOrderData,
    isLoading: isLoadingOrder,
    error: orderError,
  } = useQuery({
    queryKey: ['locationOrderDetail', returnedId],
    queryFn: async () => {
      const data = await receiveAdapter.getLocationOrderById(returnedId);

      // Mapear líneas para que tengan campos consistentes
      // La info del producto y ubicaciones ya viene en la respuesta
      const enrichedLines = (data.lines || []).map((line: any) => {
        const productInfo = line.product || null;
        return {
          ...line,
          product: productInfo,
          id: String(line.id), // ID de la línea (para identificación interna)
          product_id: line.product_id, // ID del producto (para llamadas API)
          name: productInfo?.name || 'Producto desconocido',
          code: productInfo?.code || '',
          image: productInfo?.img || '/imagen/example.webp',
          quantity: line.qty_pending, // Cantidad pendiente de ubicar
          totalQuantity: line.qty_planned, // Cantidad total planeada
          processedQuantity: line.qty_moved, // Cantidad ya movida
          batch_id: line.batch_id ?? null,
          batch: line.batch ?? null,
          // Ubicaciones ya vienen en la respuesta
          suggested_locations: line.suggested_locations || [],
          other_locations: line.other_locations || [],
        };
      });

      return {
        ...data,
        lines: enrichedLines,
      };
    },
    enabled: !!returnedId && !isNaN(returnedId),
    staleTime: 5 * 60 * 1000,
  });

  // 🎯 Adaptar datos para que el componente original los entienda
  const returnedData = locationOrderData
    ? {
        id: locationOrderData.id,
        code: `#${locationOrderData.id}`,
        status: locationOrderData.status, // Usar el string original (done, pending, etc.)
        date: locationOrderData.created_at,
        location_id: locationOrderData.cart_id,
        cart_code: locationOrderData.cart_code, // Código del carrito para mostrar en UI
        orders_count: locationOrderData.lines?.length || 0,
        lines: locationOrderData.lines ?? [],
      }
    : null;

  const isLoading = isLoadingOrder;
  const error = orderError;

  const refetch = () => {
    queryClient.invalidateQueries({
      queryKey: ['locationOrderDetail', returnedId],
    });
  };

  // 🎯 Mapear líneas a objetos que parezcan pedidos para la UI
  const adaptedOrders = useMemo(() => {
    if (!locationOrderData?.lines) return [];

    return locationOrderData.lines.map((line: any) => ({
      id: line.id,
      ref: line.product_code || `Item ${line.id}`,
      order_created_at: line.created_at,
      shop: { name: 'Almacén', image_hash: '' },
      lines: [
        {
          id: line.id,
          product_id: line.product_id,
          product: line.product,
          qty: line.qty_planned,
          // Añadimos info extra que usa useReceiveLogic
          name: line.product_name,
        },
      ],
    }));
  }, [locationOrderData]);

  // 🎯 Mutation para recepcionar líneas
  const receiveMutation = useMutation({
    mutationFn: (data: { order_id: number; lines: Array<{ product_id: number; quantity: number }> }) =>
      receiveAdapter.receiveLocationOrderLines(returnedId, data),
    onSuccess: (response) => {
      // Cualquier respuesta 200 OK sin cuerpo se considera exitosa
      toast({
        title: t('toasts.success.title'),
        description: (response as any)?.message || t('toasts.receive.success'),
        variant: 'default',
      });

      // 🎯 Invalidar queries relacionadas para forzar refetch
      queryClient.invalidateQueries({ queryKey: ['locationOrderDetail', returnedId] });
      queryClient.invalidateQueries({ queryKey: ['logisticStatusCounts'] });
    },
    onError: (error: any) => {
      console.log('🔍 useReceiveReturned - API Error:', error);

      // Extraer mensaje de error de la API
      let errorMessage = t('toasts.receive.unexpected');

      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: t('toasts.error.title'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // 🎯 Mutation para finalizar orden de ubicación
  const finalizeMutation = useMutation({
    mutationFn: () => receiveAdapter.finalizeLocationOrder(returnedId),
    onSuccess: (response) => {
      toast({
        title: t('toasts.finish.success'),
        description: (response as any)?.message || t('toasts.finish.success'),
        variant: 'default',
      });

      // 🎯 Invalidar queries relacionadas para forzar refetch
      queryClient.invalidateQueries({
        queryKey: ['locationOrderDetail', returnedId],
      });
      queryClient.invalidateQueries({ queryKey: ['logisticStatusCounts'] });
    },
    onError: (error: any) => {
      console.log('🔍 useReceiveReturned - Finalize Error:', error);

      let errorMessage = t('toasts.finish.unexpected');

      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: t('toasts.finish.error'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  return {
    returnedData,
    isLoading,
    error: error?.message || null,
    refetch,
    receiveLines: receiveMutation.mutate,
    isReceiving: receiveMutation.isPending,
    finalizeReturned: finalizeMutation.mutate,
    isFinalizing: finalizeMutation.isPending,
    orders: adaptedOrders,
    ordersLoading: false,
    ordersError: null,
  };
};
